import React, { FC, useState } from 'react';
import {
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { find, findIndex, isEmpty } from 'lodash';
import { getIn, useFormikContext } from 'formik';
import classNames from 'classnames';
import PostAddRoundedIcon from '@material-ui/icons/PostAddRounded';

import {
  DeleteIcon,
  PaddedPaper,
  SocialAccountIcon,
  ValidatedCheckbox,
  ValidatedTextField,
} from 'components';
import { Content, Network } from 'api';
import { useClient } from 'hooks';

import { getDisclosureNetwork } from '../../../posting/helpers';
import { FACEBOOK_LIMIT, LINKEDIN_LIMIT, TWITTER_LIMIT } from '../../../../constants';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
  },
  formControl: {
    'label + &': {
      marginTop: 0,
    },
  },
  safe: {
    color: theme.palette.grey[600],
  },
  warning: {
    color: theme.palette.warning.main,
  },
  error: {
    color: theme.palette.error.main,
  },
  icon: {
    marginLeft: theme.spacing(1.5),
  },
  label: {
    color: theme.palette.grey[600],
    fontSize: '11px',
  },
  textField: {
    backgroundColor: '#f7f7f8',
  },
  disclosure: {
    padding: theme.spacing(0, 2, 1, 2),
  },
  paper: {
    width: '100%',
  },
}));

const Commentary: FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext<Content>();
  const { disclosures } = useClient();

  const [isChecked, setIsChecked] = useState(false);

  function indexOfNetwork(network: Network) {
    const commentary = values.commentary || [];
    let index = findIndex(commentary, ['network', network]);
    if (index < 0) {
      index = commentary.length;
      setFieldValue(`commentary[${index}]`, { network, commentary: '' });
    }
    return index;
  }

  function getLimit(network: Network) {
    let limit = 0;

    if (!limit) {
      switch (network) {
        case 'twitter':
          limit = TWITTER_LIMIT;
          break;
        case 'linkedin':
          limit = LINKEDIN_LIMIT;
          break;
        case 'facebook':
          limit = FACEBOOK_LIMIT;
          break;
      }
    }

    return limit;
  }

  function onChangeSwitch() {
    setIsChecked(!isChecked);
  }

  function renderAdornment(length: number, limit: number) {
    const difference = limit - length;
    const className = classNames({
      [classes.safe]: difference >= 21,
      [classes.warning]: difference <= 20,
      [classes.error]: difference < 0,
    });

    return (
      <InputAdornment variant="filled" position="end">
        <Typography className={className}>
          {length}/{limit}
        </Typography>
      </InputAdornment>
    );
  }

  function renderNetwork(network: Network) {
    const index = indexOfNetwork(network);
    const fieldName = `commentary[${index}].commentary`;
    const value = getIn(values, fieldName) || '';
    let limit = getLimit(network);

    // TODO: Revisit
    const contentDisclosure = find(
      values.disclosures,
      item => item.network === getDisclosureNetwork(network),
    ) || { network: getDisclosureNetwork(network), disclosure_id: '' };
    const disclosure = find(disclosures, item => item.id === contentDisclosure.disclosure_id);

    if (disclosure) {
      limit = limit - disclosure.disclosure.length;
    }

    return (
      <Grid container alignItems="center">
        <Grid item xs={1}>
          <SocialAccountIcon active={!!value} provider={network} />
        </Grid>
        <Grid item xs={10}>
          <Grid container direction="column" className={classes.textField}>
            <Grid item>
              <ValidatedTextField
                name={fieldName}
                i18nKey="posting:commentary"
                TextFieldProps={{ margin: 'none', multiline: true, rows: 3 }}
                InputProps={{
                  classes: { formControl: classes.formControl },
                  endAdornment: renderAdornment(value.length, limit),
                }}
                onChange={(event, onChange) => onChange(event)}
              />
            </Grid>
            {!isEmpty(disclosure) && (
              <Grid item>
                <Typography className={classes.disclosure}>{disclosure.disclosure}</Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
        {!!value && (
          <Grid item xs={1}>
            <IconButton
              onClick={() => setFieldValue(fieldName, '')}
              size="small"
              className={classes.icon}>
              <DeleteIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>
    );
  }

  return (
    <PaddedPaper square className={classes.paper}>
      <Grid container alignItems="flex-start" wrap="nowrap">
        <Grid item xs={1}>
          <PostAddRoundedIcon color={isChecked ? 'inherit' : 'disabled'} />
        </Grid>
        <Grid item container direction="column" xs={10}>
          <Grid item>
            <Typography variant="subtitle1">{t('posting:commentary')}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textSecondary">{t('posting:guidelineCommentary')}</Typography>
          </Grid>
        </Grid>
        <Grid item xs={1}>
          <FormControlLabel
            control={<Switch checked={isChecked} onChange={onChangeSwitch} color="primary" />}
            label={null}
            labelPlacement="bottom"
          />
        </Grid>
      </Grid>
      {isChecked && (
        <Grid container className={classes.root}>
          <Grid item xs={1} />
          <Grid item xs={11} container direction="column" spacing={2} wrap="nowrap">
            <Grid item xs>
              {renderNetwork(Network.Twitter)}
            </Grid>
            <Grid item xs>
              {renderNetwork(Network.Linkedin)}
            </Grid>
            <Grid item xs>
              {renderNetwork(Network.Facebook)}
            </Grid>
            <Grid item xs container alignItems="center">
              <Grid item xs={1} />
              <Grid item>
                <ValidatedCheckbox
                  name="lock_commentary"
                  fullWidth
                  i18nKey="posting:lockCommentary"
                  labelClasses={{ label: classes.label }}
                  color="primary"
                  onChange={event => {
                    setFieldValue('lock_commentary', !event.target.checked);
                  }}
                  checkedFn={() => !values.lock_commentary}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </PaddedPaper>
  );
};

export default Commentary;
